"use client";

import { c as _c } from "react/compiler-runtime";
import { ChangeEvent, useEffect, useRef, useState } from "react";
import { FaCamera } from "react-icons/fa";
import { MdScanner } from "react-icons/md";
import Button from "@/components/ui/button";
import { useIsMobileOrTablet } from "@/hooks/use-is-touch-device";
import { useScannerBridge } from "@/hooks/use-scanner-bridge";
import ScannerDownloadBanner from "./scanner-download-banner";
interface ScanControlsProps {
  /** Called when a file is produced from scanning (bridge or camera). */
  onFile: (file: File) => void;
  /** Whether the controls should be disabled. */
  disabled?: boolean;
  /** Button size variant: "default" for the upload dialog, "compact" for inline use. */
  variant?: "default" | "compact";
  /** Called when a scan starts/finishes, so parents can gate actions during a scan. */
  onScanningChange?: (scanning: boolean) => void;
}

/**
 * Shared scan controls used in both the document upload dialog and note documents section.
 * Handles:
 * - Scanner Bridge detection and scan triggering (desktop)
 * - Camera capture (mobile)
 * - Download banner when bridge is not available (desktop)
 */
export default function ScanControls(t0) {
  const $ = _c(46);
  const {
    onFile,
    disabled: t1,
    variant: t2,
    onScanningChange
  } = t0;
  const disabled = t1 === undefined ? false : t1;
  const variant = t2 === undefined ? "default" : t2;
  const cameraInputRef = useRef(null);
  const [selectedScannerId, setSelectedScannerId] = useState("");
  const isTouchDevice = useIsMobileOrTablet();
  const scanner = useScannerBridge();
  let t3;
  if ($[0] !== onFile || $[1] !== scanner) {
    t3 = () => {
      if (scanner.scanState !== "complete") {
        return;
      }
      scanner.scannedPagesToFile().then(file => {
        if (file) {
          onFile(file);
          scanner.resetScan();
        }
      });
    };
    $[0] = onFile;
    $[1] = scanner;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== scanner.scanState) {
    t4 = [scanner.scanState];
    $[3] = scanner.scanState;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  useEffect(t3, t4);
  let t5;
  if ($[5] !== onScanningChange || $[6] !== scanner.isScanning) {
    t5 = () => {
      onScanningChange?.(scanner.isScanning);
      return () => onScanningChange?.(false);
    };
    $[5] = onScanningChange;
    $[6] = scanner.isScanning;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== scanner.isScanning) {
    t6 = [scanner.isScanning];
    $[8] = scanner.isScanning;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  useEffect(t5, t6);
  let t7;
  if ($[10] !== scanner.scanners[0] || $[11] !== scanner.scanners.length || $[12] !== selectedScannerId) {
    t7 = () => {
      if (scanner.scanners.length > 0 && !selectedScannerId) {
        setSelectedScannerId(scanner.scanners[0].id);
      }
    };
    $[10] = scanner.scanners[0];
    $[11] = scanner.scanners.length;
    $[12] = selectedScannerId;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== scanner.scanners || $[15] !== selectedScannerId) {
    t8 = [scanner.scanners, selectedScannerId];
    $[14] = scanner.scanners;
    $[15] = selectedScannerId;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  useEffect(t7, t8);
  let t9;
  if ($[17] !== scanner || $[18] !== selectedScannerId) {
    t9 = () => {
      if (!selectedScannerId) {
        return;
      }
      scanner.startScan({
        scannerId: selectedScannerId
      });
    };
    $[17] = scanner;
    $[18] = selectedScannerId;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  const handleScan = t9;
  let t10;
  if ($[20] !== onFile) {
    t10 = e => {
      const file_0 = e.target.files?.[0];
      if (file_0) {
        onFile(file_0);
      }
      if (cameraInputRef.current) {
        cameraInputRef.current.value = "";
      }
    };
    $[20] = onFile;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  const handleCameraCapture = t10;
  const isCompact = variant === "compact";
  let t11;
  if ($[22] !== disabled || $[23] !== handleCameraCapture || $[24] !== isCompact || $[25] !== isTouchDevice) {
    t11 = isTouchDevice && <><input ref={cameraInputRef} type="file" accept="image/*" capture="environment" onChange={handleCameraCapture} className="hidden" /><Button type="button" variant="primary" onClick={() => cameraInputRef.current?.click()} disabled={disabled} icon={isCompact ? <FaCamera className="mr-1" /> : undefined} className={isCompact ? "px-2 py-1 text-caption" : "flex w-full items-center justify-center gap-2"}>{!isCompact && <FaCamera className="h-4 w-4" />}{isCompact ? <span>{"Scan"}</span> : <span>{"Scan with Camera"}</span>}</Button></>;
    $[22] = disabled;
    $[23] = handleCameraCapture;
    $[24] = isCompact;
    $[25] = isTouchDevice;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] !== isTouchDevice || $[28] !== scanner.isAvailable) {
    t12 = !isTouchDevice && !scanner.isAvailable && <ScannerDownloadBanner />;
    $[27] = isTouchDevice;
    $[28] = scanner.isAvailable;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  let t13;
  if ($[30] !== disabled || $[31] !== handleScan || $[32] !== isCompact || $[33] !== scanner.currentPage || $[34] !== scanner.isAvailable || $[35] !== scanner.isScanning || $[36] !== scanner.scanners || $[37] !== selectedScannerId) {
    t13 = scanner.isAvailable && <div className="flex items-center gap-2">{scanner.scanners.length > 1 && <select aria-label="Select scanner" value={selectedScannerId} onChange={e_0 => setSelectedScannerId(e_0.target.value)} className="min-w-0 flex-1 truncate rounded-md border border-border bg-background px-3 py-2 text-body">{scanner.scanners.map(_temp)}</select>}<Button type="button" variant="primary" onClick={handleScan} disabled={disabled || scanner.isScanning || scanner.scanners.length === 0} icon={isCompact ? <MdScanner className="mr-1" /> : undefined} className={isCompact ? "shrink-0 px-2 py-1 text-caption" : "flex shrink-0 items-center gap-2 whitespace-nowrap"}>{!isCompact && <MdScanner className="h-4 w-4" />}{scanner.isScanning ? <span>{`Scanning${scanner.currentPage > 0 ? ` (page ${scanner.currentPage})` : "..."}`}</span> : <span>{scanner.scanners.length === 0 ? "No scanners found" : isCompact ? "Scan" : "Scan Document"}</span>}</Button></div>;
    $[30] = disabled;
    $[31] = handleScan;
    $[32] = isCompact;
    $[33] = scanner.currentPage;
    $[34] = scanner.isAvailable;
    $[35] = scanner.isScanning;
    $[36] = scanner.scanners;
    $[37] = selectedScannerId;
    $[38] = t13;
  } else {
    t13 = $[38];
  }
  let t14;
  if ($[39] !== scanner.error) {
    t14 = scanner.error && <p className="text-body text-status-error-text">{scanner.error}</p>;
    $[39] = scanner.error;
    $[40] = t14;
  } else {
    t14 = $[40];
  }
  let t15;
  if ($[41] !== t11 || $[42] !== t12 || $[43] !== t13 || $[44] !== t14) {
    t15 = <>{t11}{t12}{t13}{t14}</>;
    $[41] = t11;
    $[42] = t12;
    $[43] = t13;
    $[44] = t14;
    $[45] = t15;
  } else {
    t15 = $[45];
  }
  return t15;
}
function _temp(s) {
  return <option key={s.id} value={s.id}>{s.name}</option>;
}